import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { CONTACT_INFO, PRICING, SPECIALIST_PACKAGES } from "../data/entrepminds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    package: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        company: form.company || null,
        phone: form.phone || null,
        package: form.package || null,
        message: form.message,
      });
      toast.success("Message received. Our team will reach out within 24 hours.");
      setForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        package: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.12)_0%,transparent_60%)] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <div className="section-eyebrow mb-4">Get in touch</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05]">
            Book your <span className="gold-text">GrowthOS™</span> diagnostic.
          </h2>
          <p className="mt-6 text-[#6b6356] text-base md:text-lg leading-relaxed max-w-md">
            Tell us where you are today. We&apos;ll map where your revenue is leaking and
            what the AI-powered version of your business could look like.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={`mailto:${CONTACT_INFO.email}`}
              data-testid="contact-email-link"
              className="flex items-center gap-4 group"
            >
              <div className="w-11 h-11 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/45 flex items-center justify-center">
                <Mail size={18} className="text-[#8a6a10]" />
              </div>
              <div>
                <div className="font-mono-em text-[10px] tracking-[0.25em] uppercase text-[#6b6356]">
                  Email
                </div>
                <div className="text-[#0a0a0a] group-hover:text-[#8a6a10] transition-colors">
                  {CONTACT_INFO.email}
                </div>
              </div>
            </a>

            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#d4af37]/15 border border-[#d4af37]/45 flex items-center justify-center">
                <MapPin size={18} className="text-[#8a6a10]" />
              </div>
              <div>
                <div className="font-mono-em text-[10px] tracking-[0.25em] uppercase text-[#6b6356]">
                  HQ
                </div>
                <div className="text-[#0a0a0a]">{CONTACT_INFO.address}</div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7 em-card p-8 md:p-10 space-y-6"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="font-mono-em text-[10px] tracking-[0.25em] uppercase text-[#6b6356]">
                Name *
              </label>
              <input
                data-testid="contact-input-name"
                className="em-input"
                placeholder="Your full name"
                value={form.name}
                onChange={update("name")}
                required
              />
            </div>
            <div>
              <label className="font-mono-em text-[10px] tracking-[0.25em] uppercase text-[#6b6356]">
                Email *
              </label>
              <input
                data-testid="contact-input-email"
                type="email"
                className="em-input"
                placeholder="you@company.com"
                value={form.email}
                onChange={update("email")}
                required
              />
            </div>
            <div>
              <label className="font-mono-em text-[10px] tracking-[0.25em] uppercase text-[#6b6356]">
                Company
              </label>
              <input
                data-testid="contact-input-company"
                className="em-input"
                placeholder="Company name"
                value={form.company}
                onChange={update("company")}
              />
            </div>
            <div>
              <label className="font-mono-em text-[10px] tracking-[0.25em] uppercase text-[#6b6356]">
                Phone
              </label>
              <input
                data-testid="contact-input-phone"
                className="em-input"
                placeholder="+971…"
                value={form.phone}
                onChange={update("phone")}
              />
            </div>
          </div>

          <div>
            <label className="font-mono-em text-[10px] tracking-[0.25em] uppercase text-[#6b6356]">
              Interested in
            </label>
            <select
              data-testid="contact-select-package"
              className="em-input bg-white"
              value={form.package}
              onChange={update("package")}
            >
              <option value="">Select a package (optional)</option>
              <optgroup label="GrowthOS™ Packages">
                {PRICING.map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name} — {p.audience}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Specialist Services">
                {SPECIALIST_PACKAGES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </optgroup>
              <option value="Custom Engagement">Custom Engagement</option>
            </select>
          </div>

          <div>
            <label className="font-mono-em text-[10px] tracking-[0.25em] uppercase text-[#6b6356]">
              How can we help? *
            </label>
            <textarea
              data-testid="contact-input-message"
              className="em-input resize-none"
              rows={4}
              placeholder="Briefly tell us about your business and goals…"
              value={form.message}
              onChange={update("message")}
              required
            />
          </div>

          <button
            type="submit"
            data-testid="contact-submit-btn"
            disabled={loading}
            className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Sending…
              </>
            ) : (
              <>
                Send Message
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
